import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./NavMenu.module.css";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as HeartIcon } from "../../assets/heart.svg";
import { ReactComponent as CatalogIcon } from "../../assets/catalog.svg";
import { ReactComponent as BucketIcon } from "../../assets/bucket.svg";

import { useNavMenuStore } from "../../store/useNavMenuStore";
import { useCartStore } from "../../store/useCartStore";

interface NavMenuProps {
  onRaffleOpen?: () => void;
}

interface NavMenuItem {
  path: string;
  label: string;
  description: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

const navItems: NavMenuItem[] = [
  {
    path: "/saved",
    label: "Избранное",
    description: "Сохраненные позиции",
    Icon: HeartIcon,
  },
  {
    path: "/catalog",
    label: "Каталог",
    description: "Новые подборки и стиль",
    Icon: CatalogIcon,
  },
  {
    path: "/bucket",
    label: "Корзина",
    description: "Покупки и оформление",
    Icon: BucketIcon,
  },
];

const itemVariants = {
  hidden: { opacity: 0, y: 18, filter: "blur(8px)" },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.32,
      delay: 0.05 + index * 0.06,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  }),
  exit: {
    opacity: 0,
    y: 10,
    filter: "blur(6px)",
    transition: { duration: 0.18 },
  },
};

const NavMenu: React.FC<NavMenuProps> = ({ onRaffleOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isVisible } = useNavMenuStore();
  const { cartItemsCount } = useCartStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.innerWidth <= 500,
  );
  const toggleRef = useRef<HTMLButtonElement>(null);

  const isTabActive = (path: string) => location.pathname === path;
  const isTabBlocked = (_path: string) => false;

  const isMainPage =
    location.pathname === "/" ||
    location.pathname === "/index.html" ||
    (!["/catalog", "/saved", "/bucket"].includes(location.pathname) &&
      !location.pathname.startsWith("/banner/"));

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 500);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, isVisible, isMobileViewport]);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) {
      return;
    }

    const previousOverflow = root.style.overflow;
    const previousTouchAction = root.style.touchAction;

    if (isOpen) {
      root.style.overflow = "hidden";
      root.style.touchAction = "none";
    }

    return () => {
      root.style.overflow = previousOverflow;
      root.style.touchAction = previousTouchAction;
    };
  }, [isOpen]);

  useEffect(() => {
    const WA = window.Telegram?.WebApp;

    const placeToggle = () => {
      const safeTop =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((WA as any)?.safeAreaInset?.top as number | undefined) ?? 0;

      if (toggleRef.current) {
        toggleRef.current.style.top = `${safeTop + 8}px`;
      }
    };

    placeToggle();
    const timeoutId = setTimeout(placeToggle, 100);

    WA?.onEvent?.("safe_area_changed", placeToggle);
    WA?.onEvent?.("viewport_changed", placeToggle);

    return () => {
      clearTimeout(timeoutId);
      WA?.offEvent?.("safe_area_changed", placeToggle);
      WA?.offEvent?.("viewport_changed", placeToggle);
    };
  }, [location.pathname]);

  if (!isVisible || isMainPage || !isMobileViewport) {
    return null;
  }

  const handleTabClick = (path: string) => {
    if (path === "/raffles" && onRaffleOpen) {
      onRaffleOpen();
      setIsOpen(false);
      return;
    }

    if (isTabBlocked(path)) {
      return;
    }

    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      <motion.button
        ref={toggleRef}
        type="button"
        className={`${styles.navMenuToggle} ${
          isOpen ? styles["navMenuToggle--open"] : ""
        }`}
        aria-label={isOpen ? "Закрыть меню" : "Открыть меню"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        initial={{ opacity: 0, y: -18, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
        >
          <span
            className={`${styles.navMenuToggle__icon} ${
              cartItemsCount > 0 ? styles["navMenuToggle__icon--hasCart"] : ""
            }`}
            aria-hidden="true"
          >
            <span className={styles.navMenuToggle__line} />
            <span className={styles.navMenuToggle__line} />
            <span className={styles.navMenuToggle__line} />
          </span>
        {cartItemsCount > 0 && <span className={styles.navMenuToggle__badge} />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.button
              type="button"
              className={styles.navMenuBackdrop}
              aria-label="Закрыть меню"
              onClick={() => setIsOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.24 }}
            />

            <motion.div
              className={styles.navMenuPanel}
              initial={{
                opacity: 0,
                y: -18,
                scale: 0.95,
                filter: "blur(10px)",
              }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, scale: 0.97, filter: "blur(8px)" }}
              transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className={styles.navMenuPanel__header}>
                <div className={styles.navMenuPanel__title}>
                  Быстрый переход
                </div>
              </div>

              <div className={styles.navMenuPanel__items}>
                {navItems.map((item, index) => {
                  const isActive = isTabActive(item.path);
                  const isBlocked = isTabBlocked(item.path);
                  const ItemIcon = item.Icon;

                  return (
                    <motion.button
                      key={item.path}
                      type="button"
                      custom={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={`${styles.navMenuItem} ${
                        isActive ? styles["navMenuItem--active"] : ""
                      } ${isBlocked ? styles["navMenuItem--blocked"] : ""}`}
                      onClick={() => handleTabClick(item.path)}
                    >
                      <span className={styles.navMenuItem__iconWrap}>
                        <ItemIcon className={styles.navMenuItem__icon} />
                        {item.path === "/bucket" && cartItemsCount > 0 && (
                          <span className={styles.navMenuItem__badge}>
                            {cartItemsCount}
                          </span>
                        )}
                      </span>

                      <span className={styles.navMenuItem__copy}>
                        <span className={styles.navMenuItem__title}>
                          {item.label}
                        </span>
                        <span className={styles.navMenuItem__description}>
                          {item.description}
                        </span>
                      </span>

                      <span className={styles.navMenuItem__meta}>
                        {isActive ? "Сейчас" : "Открыть"}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavMenu;
